import s from './ExploreCard.module.sass';

import React, { FC } from 'react';
import {
  AuthPopoverState,
  MineExploreCard,
  MineExploreCardContentTypeEnum,
} from '$store/models';
import classNames from 'classnames';
import { useTranslator, useUserInfo } from '$hooks';
import { UnitLink, UnitName } from '$shared/components';
import { IcChevron16, IcLogo40, IcPlay28 } from '$assets';
import { _confirm, formatBigNumber, statReachGoal } from '$utils';
import { toastActions } from '$store/toast';
import { useDispatch, useSelector } from 'react-redux';
import { Api } from '$api';
import { useRouter } from 'react-router5';
import { MINE } from '$shared/constants/pages';
import { mineActions } from '$store/mine';
import { showModal } from '$navigation/helper';
import { profileActions } from '$store/profile';
import { authSelector } from '$store/auth';
import { mainActions } from '$store/main';
import { MineExploreNoFunds } from '$core/MineExplore/MineExploreNoFunds/MineExploreNoFunds';

export const ExploreCard: FC<{ card: MineExploreCard; canBuy: boolean }> = ({
  card,
  canBuy,
}) => {
  const dispatch = useDispatch();
  const owner = useUserInfo(card.ownerId);
  const router = useRouter();
  const { isLogged } = useSelector(authSelector);
  const t = useTranslator();

  const handleOpen = () => {
    statReachGoal('mine_explore_press_card');

    if (card.isBought) {
      showModal(`mineExploreCard${card.id}`);
    } else {
      if (!canBuy) {
        dispatch(toastActions.setToastFail(t('mine_explore_level_locked')));
        return;
      }

      if (!isLogged) {
        dispatch(profileActions.setAuthPopoverState(AuthPopoverState.REGISTER));
        return;
      }

      dispatch(toastActions.setToastLoading());
      Api.post<any, any>(`/mine/explore/${card.id}/buy`)
        .then((resp) => {
          dispatch(mineActions.updateExploreCard(resp.card));
          dispatch(toastActions.hideToast());

          const el = document.querySelector(`._mine_explore_cards_${card.level}`);
          if (el) {
            el.scrollTo({
              left: 0,
              behavior: 'smooth',
            });
          }

          statReachGoal('mine_explore_buy');
        })
        .catch((e) => {
          if (e.code === 'insufficient_funds') {
            dispatch(toastActions.hideToast());
            dispatch(mainActions.setPopup(<MineExploreNoFunds />));
          } else {
            dispatch(toastActions.setToastFail(e.message));
          }
        });
    }
  };

  return (
    <div className={s.wrap}>
      <div className={s.imageWrap} onClick={handleOpen}>
        <div className={s.price}>
          <IcLogo40 />
          <div className={s.priceValue}>{formatBigNumber(card.price)}</div>
        </div>
        {!card.isBought && (
          <>
            <div
              className={classNames({
                [s.showBtn]: true,
                [s.showBtnDisabled]: !canBuy,
              })}
            >
              {t('mine_explore_unlock')}
            </div>
          </>
        )}
        <div
          className={classNames({
            [s.image]: true,
            [s.imageBlur]: !card.isBought,
          })}
          style={{
            backgroundImage: `url(${card.isBought ? card.contentImageUrl : card.thumb})`,
          }}
        />
        {card.contentType === MineExploreCardContentTypeEnum.video && (
          <div className="ExternalVideo__play_btn">
            <IcPlay28 />
          </div>
        )}
      </div>
      <UnitLink unit={owner} className={s.owner}>
        <div
          className={s.ownerPhoto}
          style={{ backgroundImage: `url(${owner.photo})` }}
        ></div>
        <div className={s.ownerName}>
          <UnitName unit={owner} />
        </div>
        <IcChevron16 className={s.ownerChevron} />
      </UnitLink>
    </div>
  );
};
